import {
  MicroProduct,
  MicroProductsSlot,
  useMicroProductsLoader,
} from '@domain/microproducts/loaders/use-microproducts-loader';
import { useMemo } from 'react';

interface UseMicroProductScreenLoaderParams {
  productId?: string;
  /** Restrict the lookup to a single slot. When omitted, searches all slots.
   *  Scoped surfaces (e.g. pricing) pass their slot so a product only resolves
   *  if it's actually configured for them — making slot removal an off switch. */
  slot?: MicroProductsSlot;
}

interface UseMicroProductScreenLoaderResult {
  error: Error | null;
  loading: boolean;
  product: MicroProduct | null;
}

/**
 * Loader for the MicroProductLoaderScreen.
 * Finds a specific product by ID, optionally scoped to a single slot.
 *
 * @param params.productId - The product ID to find
 * @param params.slot - Optional slot to restrict the lookup to
 */
export const useMicroProductScreenLoader = ({
  productId,
  slot,
}: UseMicroProductScreenLoaderParams): UseMicroProductScreenLoaderResult => {
  const { slots, loading, error } = useMicroProductsLoader();

  const product = useMemo(() => {
    if (!productId) {
      return null;
    }
    const searchSlots = slot ? slots.filter((s) => s.slot === slot) : slots;
    return (
      searchSlots.flatMap((s) => s.products).find((p) => p.id === productId) ??
      null
    );
  }, [slots, productId, slot]);

  return {
    error,
    loading,
    product,
  };
};
